import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  GetChangeRequestsDocument,
  type GetChangeRequestsQuery,
  type GetChangeRequestsQueryVariables,
} from "~/features/changeRequests/gqls";
import { useTenant } from "~/features/tenant/context";

interface TrackedJob {
  changeRequestIds: string[];
  startTime: number;
}

const POLL_MS = 3000;
const TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Tracks change requests submitted to a batch review job and polls their status until
 * each leaves "open" (committed/rejected) or a 10-minute timeout elapses. Ported from V1
 * `useBatchJobProcessing`. `Date.now()` is fine here — this is runtime-only state.
 */
export function useChangeRequestProcessing() {
  const { tenantId } = useTenant();
  const [jobs, setJobs] = useState<Record<string, TrackedJob>>({});
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [fetchChangeRequests] = useLazyQuery<GetChangeRequestsQuery, GetChangeRequestsQueryVariables>(
    GetChangeRequestsDocument,
    { fetchPolicy: "network-only", errorPolicy: "ignore" },
  );

  const trackJob = useCallback((jobRunId: string, changeRequestIds: string[]) => {
    setJobs((prev) => ({ ...prev, [jobRunId]: { changeRequestIds, startTime: Date.now() } }));
  }, []);

  const stopTracking = useCallback((jobRunId: string) => {
    setJobs((prev) => {
      const { [jobRunId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  useEffect(() => {
    if (Object.keys(jobs).length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    let cancelled = false;
    const stopIfActive = (jobRunId: string) => {
      if (cancelled) return;
      stopTracking(jobRunId);
    };
    const poll = async () => {
      const entries = Object.entries(jobs);
      const now = Date.now();
      for (const [jobRunId, job] of entries) {
        if (now - job.startTime > TIMEOUT_MS) {
          stopIfActive(jobRunId);
        }
      }
      if (!tenantId || cancelled) return;

      const idJobs = entries.filter(([, job]) => job.changeRequestIds.length > 0);
      const ids = idJobs.flatMap(([, job]) => job.changeRequestIds);
      if (ids.length > 0) {
        const res = await fetchChangeRequests({
          variables: { tenantId, limit: 100, where: { id: { _in: ids } }, order_by: { updatedAt: "DESC" } },
        });
        const rows = res.data?.changeRequests ?? [];
        for (const [jobRunId, job] of idJobs) {
          const settled = job.changeRequestIds.every((id) => {
            const cr = rows.find((r) => r.id === id);
            return cr ? cr.statusId !== "open" : false;
          });
          if (settled) stopIfActive(jobRunId);
        }
      }
    };
    void poll();
    intervalRef.current = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobs, tenantId, fetchChangeRequests, stopTracking]);

  const processingIds = new Set(Object.values(jobs).flatMap((j) => j.changeRequestIds));
  const isProcessing = useCallback((id: string) => processingIds.has(id), [processingIds]);

  return { trackJob, isProcessing };
}
